import React from 'react';
import axios from 'axios';
import { Listing, ListingRow } from 'components/Listing/Listing';
import {
  LeftRightItem,
  TopBottomItem,
  ImageItem,
  TextAreaItem,
  TextItem,
} from 'components/Listing/ListingItem';
import {
  PageListing,
  PageListingRow,
  PageListingItem,
  PageListingDescriptionItem,
  PageListingDivider,
} from 'components/Listing/PageListing';
import { Button } from '@lightspeed/flame/Button';
import { IconClipboard } from '@lightspeed/flame/Icon/Clipboard';
import Format from 'helpers/format';
import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';

import Loading from 'pages/views/generic/Loading';
import LoadingError from 'pages/views/generic/LoadingError';
import { getDateFormat } from 'pages/utils/time';

import PaymentInfoReportTransactionDetails from './PaymentInfoReportTransactionDetails';
import PaymentInfoReportRefunds from './PaymentInfoReportRefunds';

import timezoneConvert from '../utils/timezoneConvert';

const updateBreadcrumbs = location => {
  const newCrumb = {
    text: 'Payment Details',
    path: location.pathname,
  };
  let [...crumbs] = window.breadcrumbs || [];

  // We need to update the legacy breadcumbs with "something" otherwise
  // navigation to gui_def views will not funciton correctly since our
  // Topbar crumbs length and the legacy breadcrumbs length will be out of sync.
  if (window.breadcrumbs) {
    window.breadcrumbs.push(newCrumb);
  }

  // Build the Topbar crumbs object from the breadcrumbs (see Topbar component for props)
  crumbs = crumbs.map(crumb => ({
    text: crumb.title || crumb.text,
    name: crumb.name,
    type: crumb.type,
  }));
  crumbs.push(newCrumb);

  // Notify
  window.merchantos.asyncSubscribe.notifySubscribers('react.mount.topbar', {
    props: {
      crumbs,
    },
  });
};

const STRINGS = {
  ERROR: {
    FALLBACK: `This page is temporarily unavailable. Try again and if the problem persists, contact Support.`,
  },
};

export default class PaymentInfoReport extends React.Component {
  /**
   * Cuts long strings off at 40 characters and appends "..."
   *
   * @param value - The value to potentially truncate
   * @returns {String} - The potentially truncated string
   */
  static truncateAt40Chars(value) {
    let newValue = value;

    if (newValue.length > 40) {
      newValue = `${newValue.substr(0, 39)}...`;
    }

    return newValue;
  }

  /**
   * Every API call starts with '/API/Account/ACCOUNT_ID/'.
   * This puts that base into a common area.
   *
   * @returns {String} The Base API URL
   */
  static getBaseApiUrl() {
    return `/API/Account/${get(window.window.merchantos, 'session.sessData.systemCustomerID')}/`;
  }

  /**
   * We try to do API calls in chunks whenever possible so that we don't
   * wait around for no reason. When the page loads, all we can grab is the
   * SalePayment and the Session, so do that at the same time.
   *
   * @param salePaymentID
   * @returns {Array} Array of promises
   */
  static getSalePaymentData(salePaymentID) {
    return salePaymentID && parseInt(salePaymentID, 10) > 0
      ? axios.get(`${PaymentInfoReport.getBaseApiUrl()}SalePayment/${salePaymentID}.json`)
      : Promise.resolve(null);
  }

  /**
   * Retrieves the Payment data from the Payment Service.
   *
   * @param {string} paymentID The payment service ID for the payment
   */
  static getPaymentData(paymentID) {
    return paymentID
      ? axios.get(`${PaymentInfoReport.getBaseApiUrl()}Payment/${paymentID}.json`)
      : Promise.resolve(null);
  }

  /**
   * Retrieves the Sale data from the Retail API.
   *
   * @param {string} saleID The ID of the sale
   */
  static getSaleData(saleID) {
    return saleID && parseInt(saleID, 10) > 0
      ? axios.get(`${PaymentInfoReport.getBaseApiUrl()}Sale/${saleID}.json`)
      : Promise.resolve(null);
  }

  /**
   * Retrieves the Payment Type data from the Retail API
   *
   * @param {string} paymentTypeID The ID of the payment type
   */
  static getPaymentType(paymentTypeID) {
    return paymentTypeID && parseInt(paymentTypeID, 10) > 0
      ? axios.get(
          `${PaymentInfoReport.getBaseApiUrl()}PaymentType/${paymentTypeID}.json?archived=true`
        )
      : Promise.resolve(null);
  }

  /**
   * Retrieves the Customer data from the Retail API
   *
   * @param {string} customerID The ID of the customer
   */
  static getCustomerData(customerID) {
    return customerID && parseInt(customerID, 10) > 0
      ? axios.get(
          `${PaymentInfoReport.getBaseApiUrl()}Customer/${customerID}.json?load_relations=all`
        )
      : Promise.resolve(null);
  }

  /**
   * Retrieves the Employee data from the Retail API
   *
   * @param {string} employeeID The ID of the employee
   */
  static getEmployeeData(employeeID) {
    return employeeID && parseInt(employeeID, 10) > 0
      ? axios.get(
          `${PaymentInfoReport.getBaseApiUrl()}Employee/${employeeID}.json?load_relations=all`
        )
      : Promise.resolve(null);
  }

  /**
   * Retrieves the Register data from the Retail API
   *
   * @param {string} registerID The ID of the register
   */
  static getRegisterData(registerID) {
    return registerID && parseInt(registerID, 10) > 0
      ? axios.get(
          `${PaymentInfoReport.getBaseApiUrl()}Register/${registerID}.json?load_relations=all`
        )
      : Promise.resolve(null);
  }

  /**
   * Retrieves the Shop data from the Retail API
   *
   * @param {string} shopID The ID of the shop
   */
  static getShopData(shopID) {
    return shopID && parseInt(shopID, 10) > 0
      ? axios.get(`${PaymentInfoReport.getBaseApiUrl()}Shop/${shopID}.json?load_relations=all`)
      : Promise.resolve(null);
  }

  /**
   * We need to display the customer in a user-friendly way.
   * Ideally we want to show the First Name and/or Last Name if they
   * exist; if they don't, we just display their phone number
   * with the following priority:
   *
   * Mobile > Work > Home > Fax > Pager
   *
   * @param customerResponse
   * @returns {String} UI-friendly Customer Name or Number
   */
  static formatCustomer(customerResponse) {
    let ret;

    if (customerResponse) {
      const customer = customerResponse.data.Customer;
      if (customer.firstName || customer.lastName) {
        ret = `${customer.firstName} ${customer.lastName}`;
      } else if (typeof customer.Contact.Emails.ContactEmail !== 'undefined') {
        const emails = customer.Contact.Emails.ContactEmail;

        // emails is either an array or an object; if it's an object,
        // then there's only one entry, so use that one
        if (!!emails && emails.constructor === Object) {
          ret = emails.address;
        } else {
          // Otherwise, use the primary email
          ret = emails.filter(email => email.useType === 'Primary')[0];

          ret = ret.address;
        }
      } else if (typeof customer.Contact.Phones === 'object') {
        const phones = customer.Contact.Phones.ContactPhone;

        // phones is either an array or an object; if it's an object,
        // then there's only one entry, so use that one
        if (!!phones && phones.constructor === Object) {
          ret = phones.number;
        } else {
          // Otherwise we have to find and use the correct one
          ret = phones.filter(phone => phone.useType === 'Mobile')[0];

          if (!ret) {
            ret = phones.filter(phone => phone.useType === 'Work')[0];
          }

          if (!ret) {
            ret = phones.filter(phone => phone.useType === 'Home')[0];
          }

          if (!ret) {
            ret = phones.filter(phone => phone.useType === 'Fax')[0];
          }

          if (!ret) {
            ret = phones.filter(phone => phone.useType === 'Pager')[0];
          }

          ret = ret.number;
        }
      } else {
        ret = 'N/A';
      }
    } else {
      ret = 'N/A';
    }

    ret = PaymentInfoReport.truncateAt40Chars(ret);

    return ret;
  }

  /**
   * Grabs the Employee First/Last name from the API response
   *
   * @param employeeResponse
   * @returns {string} Employee First Name and/or Last Name
   */
  static formatEmployee(employeeResponse) {
    const firstName = get(employeeResponse, 'data.Employee.firstName');
    const lastName = get(employeeResponse, 'data.Employee.lastName');
    let employee = firstName && lastName ? `${firstName} ${lastName}` : 'N/A';

    employee = PaymentInfoReport.truncateAt40Chars(employee);

    return employee;
  }

  /**
   * Grabs the Gateway response of each payment and formats them accordingly
   *
   * @param paymentData
   * @returns {string}
   */
  static formatGatewayResponse(currentPaymentData) {
    let currentPaymentTransactions = get(currentPaymentData, 'Transactions.PaymentTransaction', []);
    if (!Array.isArray(currentPaymentTransactions)) {
      currentPaymentTransactions = [currentPaymentTransactions];
    }

    let gatewayResponse = '';
    currentPaymentTransactions.forEach(currentPaymentTransaction => {
      if (get(currentPaymentTransaction, 'CardTransactionDetails.gatewayResponse')) {
        gatewayResponse += atob(currentPaymentTransaction.CardTransactionDetails.gatewayResponse);
      }
    });

    return gatewayResponse;
  }

  /**
   * Grabs the Register Name from the API response
   *
   * @param registerResponse
   * @returns {string} Register Name
   */
  static formatRegister(registerResponse) {
    return get(registerResponse, 'data.Register.name', 'N/A');
  }

  /**
   * Grabs the Shop Name from the API response
   *
   * @param shopResponse
   * @returns {string} Shop Name
   */
  static formatShop(shopResponse) {
    return get(shopResponse, 'data.Shop', {});
  }

  /**
   * Grabs the sale ID from the API response
   *
   * @param {Object} paymentData The payment data retrieved via the API
   */
  static formatSaleID(paymentData) {
    const saleID = get(paymentData, 'saleID', '0');

    return paymentData && saleID !== '0' ? saleID : 'N/A';
  }

  /**
   * Converts the Base64 returned from the image response into an image
   *
   * @param imageData
   * @returns {string} Image src
   */
  static getBase64ImageLink(imageData) {
    let imageLink = null;

    if (imageData) {
      imageLink = `data:${imageData.dataType};base64,${imageData.data}`;
    }

    return imageLink;
  }

  /**
   * Retrieves the Refunded payment from the Payment Service.
   *
   * @param {string} refundedPaymentID The refunded payment ID
   */
  static getRefundedPaymentData(refundedPaymentID) {
    return refundedPaymentID
      ? PaymentInfoReport.getPaymentData(refundedPaymentID)
      : Promise.resolve(null);
  }

  constructor(props) {
    super(props);

    const { salePaymentId } = get(props, 'match.params', {});

    this.state = {
      isLoading: true,
      isSalePayment: !isUndefined(salePaymentId),
    };
  }

  componentDidMount() {
    const { id, salePaymentId } = this.props.match.params;
    const { isSalePayment } = this.state;
    const { location } = this.props;

    updateBreadcrumbs(location);

    const paymentDataPromise = new Promise((resolve, reject) => {
      if (isSalePayment) {
        PaymentInfoReport.getSalePaymentData(salePaymentId)
          .then(salePaymentResponse => {
            const salePaymentData = get(salePaymentResponse, 'data.SalePayment');
            axios
              .all([
                PaymentInfoReport.getPaymentData(get(salePaymentData, 'paymentID')),
                PaymentInfoReport.getSaleData(get(salePaymentData, 'saleID')),
                PaymentInfoReport.getPaymentType(get(salePaymentData, 'paymentTypeID')),
              ])
              .then(responses => {
                resolve({
                  paymentData: get(responses[0], 'data.Payment'),
                  saleData: get(responses[1], 'data.Sale'),
                  paymentTypeData: get(responses[2], 'data.PaymentType'),
                  salePaymentData,
                });
              })
              .catch(e => {
                reject(e);
              });
          })
          .catch(e => {
            reject(e);
          });
      } else {
        PaymentInfoReport.getPaymentData(id)
          .then(paymentDataResponse => {
            const paymentData = get(paymentDataResponse, 'data.Payment');
            axios
              .all([
                PaymentInfoReport.getSaleData(get(paymentData, 'saleID')),
                PaymentInfoReport.getPaymentType(get(paymentData, 'SalePayment.paymentTypeID')),
                PaymentInfoReport.getSalePaymentData(get(paymentData, 'SalePayment.salePaymentID')),
              ])
              .then(responses => {
                resolve({
                  paymentData,
                  saleData: get(responses[0], 'data.Sale'),
                  paymentTypeData: get(responses[1], 'data.PaymentType'),
                  salePaymentData: get(responses[2], 'data.SalePayment'),
                });
              })
              .catch(e => {
                reject(e);
              });
          })
          .catch(e => {
            reject(e);
          });
      }
    });

    paymentDataPromise
      .then(({ paymentData, saleData, paymentTypeData, salePaymentData }) => {
        const gatewayResponse = PaymentInfoReport.formatGatewayResponse(paymentData, []);
        const gateway = get(paymentData, 'cardPaymentRequest.gateway', 'N/A');
        const customerRefusedToSign =
          get(paymentData, 'Transactions.PaymentTransaction.parameters.signatureStatusCode') ===
          'SignatureRequiredCancelledByCardholder';
        const imageLink = get(paymentData, 'Transactions.PaymentTransaction.Signature')
          ? PaymentInfoReport.getBase64ImageLink(
              paymentData.Transactions.PaymentTransaction.Signature
            )
          : null;
        const paymentType = get(paymentTypeData, 'name', 'N/A');

        let paymentTransactionData = paymentData.Transactions.PaymentTransaction;
        if (!Array.isArray(paymentTransactionData)) {
          paymentTransactionData = [paymentTransactionData];
        }

        axios
          .all([
            PaymentInfoReport.getCustomerData(get(saleData, 'customerID')),
            PaymentInfoReport.getEmployeeData(get(saleData, 'employeeID')),
            PaymentInfoReport.getRegisterData(get(paymentData, 'registerID')),
            PaymentInfoReport.getShopData(get(paymentData, 'shopID')),
            PaymentInfoReport.getRefundedPaymentData(get(paymentData, 'refundedPaymentID')),
          ])
          .then(async responses => {
            const customer = PaymentInfoReport.formatCustomer(responses[0]);
            const employee = PaymentInfoReport.formatEmployee(responses[1]);
            const register = PaymentInfoReport.formatRegister(responses[2]);
            const shop = PaymentInfoReport.formatShop(responses[3]);
            const saleID = PaymentInfoReport.formatSaleID(paymentData);

            const refundedPaymentData = get(responses[4], 'data.Payment');
            const paymentDataWithRefunds = paymentData;
            const paymentRefunds = get(paymentDataWithRefunds, 'Refunds.Payment');
            if (refundedPaymentData && paymentRefunds) {
              paymentDataWithRefunds.Refunds.Payment.push(refundedPaymentData);
            } else if (refundedPaymentData) {
              paymentDataWithRefunds.Refunds = {
                Payment: [refundedPaymentData],
              };
            } else if (paymentRefunds && !Array.isArray(paymentRefunds)) {
              paymentDataWithRefunds.Refunds = {
                Payment: [paymentRefunds],
              };
            }

            const dateFormat = await getDateFormat();

            this.setState({
              isLoading: false,
              customer,
              customerID: get(saleData, 'customerID', null),
              customerRefusedToSign,
              date: timezoneConvert(
                paymentData.createdAt,
                get(shop, 'timeZone', 'UTC'),
                dateFormat
              ),
              time: timezoneConvert(paymentData.createdAt, get(shop, 'timeZone', 'UTC'), 'h:mm A'),
              employee,
              employeeID: get(salePaymentData, 'employeeID', null),
              gateway,
              gatewayResponse,
              hasAdminShopRight:
                typeof get(window.merchantos, 'session.sessData.Rights.admin_shops') !==
                'undefined',
              imageLink,
              paymentAmount: Format.formatPrice(paymentData.totalAmount),
              paymentID: isSalePayment ? salePaymentId : id,
              // eslint-disable-next-line react/no-unused-state
              salePaymentId: get(salePaymentData, 'salePaymentID', null),
              paymentTransactionData: paymentTransactionData[paymentTransactionData.length - 1],
              paymentData: paymentDataWithRefunds,
              paymentType,
              register,
              saleID,
              shopName: get(shop, 'name', 'N/A'),
            });
          })
          .catch(e => {
            this.showErrorMessage(e);
          });
      })
      .catch(e => {
        this.showErrorMessage(e);
      });
  }

  /**
   * Outputs an error message when the report fails to load.
   * This is needed in several places, so consolidate the code here.
   */
  showErrorMessage(
    { message } = {
      message: STRINGS.ERROR.FALLBACK,
    }
  ) {
    this.setState({
      isLoading: false,
      error: message,
    });
  }

  renderPage() {
    const {
      isLoading,
      customer,
      customerID,
      customerRefusedToSign,
      date,
      time,
      employee,
      employeeID,
      gateway,
      gatewayResponse,
      hasAdminShopRight,
      imageLink,
      paymentAmount,
      paymentID,
      paymentTransactionData,
      paymentData,
      paymentType,
      register,
      saleID,
      shopName,
      isSalePayment,
    } = this.state;
    const accountID = get(window.merchantos, 'session.sessData.systemCustomerID');

    const copyToClipboardBtnFunc = () => {
      document.querySelector('#gateway-response').select();
      document.execCommand('copy');
    };

    const page = !isLoading ? (
      <PageListing>
        <PageListingRow>
          <PageListingDescriptionItem
            mainText="Payment Summary"
            secondaryText="Main information of the completed payment."
          />
          <PageListingItem>
            <Listing>
              <ListingRow>
                <TopBottomItem top="Payment Type" bottom={paymentType} />
                <TopBottomItem top="Payment Amount" bottom={paymentAmount} notranslate />
              </ListingRow>
              <ListingRow>
                <LeftRightItem left="Payment ID" right={paymentID} notranslate />
                <LeftRightItem left="Date" right={date} notranslate />
              </ListingRow>
              <ListingRow>
                <LeftRightItem
                  left="Sale ID"
                  right={saleID}
                  href="/?form_name=view&name=transaction.views.transaction&id="
                  id={saleID}
                  notranslate
                />
                <LeftRightItem left="Time" right={time} notranslate />
              </ListingRow>
              <ListingRow>
                <LeftRightItem
                  left="Customer"
                  right={customer}
                  href="/?form_name=view&name=customer.views.customer&id="
                  id={customerID}
                  notranslate
                />
                <LeftRightItem left="Register" right={register} notranslate />
              </ListingRow>
              <ListingRow>
                <LeftRightItem
                  left="Employee"
                  right={employee}
                  href="/?form_name=view&name=admin.views.employee&id="
                  id={employeeID}
                  notranslate
                />
                <LeftRightItem left="Shop" right={shopName} notranslate />
              </ListingRow>
            </Listing>
          </PageListingItem>
        </PageListingRow>
        <PageListingDivider />
        <PaymentInfoReportTransactionDetails
          accountID={accountID}
          paymentTransactionData={paymentTransactionData}
          paymentData={paymentData}
          gateway={gateway}
        />
        {imageLink ? (
          <PageListingRow>
            <PageListingDescriptionItem
              mainText="Customer Signature"
              secondaryText="Customer's digital signature that authorized the integrated payment."
            />
            <PageListingItem>
              <Listing>
                <ListingRow>
                  <ImageItem imageLink={imageLink} className="min-height-100px" />
                </ListingRow>
              </Listing>
            </PageListingItem>
          </PageListingRow>
        ) : null}
        {!imageLink && customerRefusedToSign ? (
          <PageListingRow>
            <PageListingDescriptionItem
              mainText="Customer Signature"
              secondaryText="Customer's digital signature that authorized the integrated payment."
            />
            <PageListingItem>
              <Listing>
                <ListingRow>
                  <TextItem
                    className="react-listing-text-cell-wrapper center"
                    value="The signature prompt on the terminal was cancelled by the customer."
                  />
                </ListingRow>
              </Listing>
            </PageListingItem>
          </PageListingRow>
        ) : null}
        {hasAdminShopRight && gatewayResponse ? <PageListingDivider /> : null}
        {hasAdminShopRight && gatewayResponse ? (
          <PageListingRow>
            <PageListingDescriptionItem
              mainText="Gateway Response"
              secondaryText="Integrated payment's communication code to better understand and troubleshoot the response returned from the gateway."
            >
              {!window.isGriffin() ? (
                <Button id="copy-to-clipboard" onClick={copyToClipboardBtnFunc}>
                  <IconClipboard />
                  Copy to Clipboard
                </Button>
              ) : null}
            </PageListingDescriptionItem>
            <PageListingItem>
              <Listing>
                <ListingRow>
                  <TextAreaItem id="gateway-response" value={gatewayResponse} />
                </ListingRow>
              </Listing>
            </PageListingItem>
          </PageListingRow>
        ) : null}
        {get(paymentData, 'Refunds.Payment', []).length ? <PageListingDivider /> : null}
        {get(paymentData, 'Refunds.Payment', []).length ? (
          <PaymentInfoReportRefunds
            refunds={paymentData.Refunds.Payment}
            dateTimeFormat={window.merchantos.locale.date_time_format}
            isSalePayment={isSalePayment}
          />
        ) : null}
      </PageListing>
    ) : (
      <Loading />
    );

    return page;
  }

  render() {
    const { error } = this.state;

    return (
      <div className="react-listing-page-wrapper cirrus">
        {error ? <LoadingError>{error}</LoadingError> : this.renderPage()}
      </div>
    );
  }
}
